import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { useAdminCounts, type AdminCounts } from "../../api/admin";
import { useMe } from "../../auth/session";
import { PageHead } from "../../components/ui";
import "../../styles/admin.css";

export interface AdminSection {
  href: string;
  label: string;
  /** The registry row that governs this section, where there is one: those
   * three are configurable in the visibility matrix, so hiding Users there
   * has to take this link away as well as its nav entry. The rest have no
   * row and are shown to whoever reaches the section at all. */
  key?: string;
  /** Which of the server's counts numbers it; settings and the back-load
   * keep no list, so they have none. */
  count?: keyof AdminCounts;
}

/** The side nav, grouped the way the office thinks of the reference data:
 * who signs in, what things cost, where the crews meet, and the records. */
export const ADMIN_GROUPS: { label: string; sections: AdminSection[] }[] = [
  {
    label: "People and access",
    sections: [
      { href: "/providers", label: "Providers", key: "providers", count: "providers" },
      { href: "/users", label: "Users", key: "users", count: "users" },
    ],
  },
  {
    label: "Parts and prices",
    sections: [
      { href: "/admin/spares", label: "Spares", key: "admin_spares", count: "spares" },
      { href: "/admin/price-lists", label: "Price lists", count: "price_lists" },
      { href: "/admin/labour-allowances", label: "Labour allowances" },
      { href: "/admin/flange-tables", label: "Flange tables", count: "flange_tables" },
    ],
  },
  {
    label: "Places",
    sections: [{ href: "/admin/meeting-points", label: "Meeting points", count: "meeting_points" }],
  },
  {
    label: "Records and settings",
    sections: [
      { href: "/admin/settings", label: "Settings" },
      { href: "/admin/audit", label: "Audit", count: "audit" },
      { href: "/admin/quotations-backload", label: "Back-load quotes" },
    ],
  },
];

export const ADMIN_SECTIONS: AdminSection[] = ADMIN_GROUPS.flatMap((group) => group.sections);

/** Administration: one side nav down every reference-data screen, so the
 * office moves between them without going back to the masthead. Every route
 * under it is office-only and hidden in demo mode by the server; the nav is
 * reached from the account menu, not the main nav. */
export default function AdminLayout({
  title,
  sub,
  actions,
  children,
}: {
  title: string;
  sub?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const me = useMe();
  const counts = useAdminCounts();
  const allowed = new Set((me.data?.screens ?? []).map((screen) => screen.key));
  const groups = ADMIN_GROUPS.map((group) => ({
    ...group,
    sections: group.sections.filter((section) => section.key === undefined || allowed.has(section.key)),
  })).filter((group) => group.sections.length > 0);

  return (
    <div className="page-bleed admin-page">
      <div className="split admin-split">
        <aside className="split-rail">
          <nav className="side-nav" aria-label="Administration">
            {groups.map((group) => (
              <div key={group.label} className="side-section">
                <div className="side-group">{group.label}</div>
                {group.sections.map((section) => (
                  <NavLink
                    key={section.href}
                    to={section.href}
                    className={({ isActive }) => (isActive ? "side-link current" : "side-link")}
                  >
                    <span>{section.label}</span>
                    {section.count !== undefined && counts.data ? (
                      <span className="side-count">{counts.data[section.count].toLocaleString("en-ZA")}</span>
                    ) : null}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
        </aside>
        <div className="split-main admin-main">
          <PageHead title={title} sub={sub} actions={actions} />
          {children}
        </div>
      </div>
    </div>
  );
}
